/**
 * UDS service for `UilmTranslationJob` records — the durable state for
 * UILM Auto-Translate jobs.
 *
 * The frontend writes a `running` row when an admin clicks Auto-Translate;
 * the webhook receiver (handled outside this app) flips it to
 * `completed` / `failed`. The Translations Tab's status banner polls
 * `getLatestTranslationJobForTemplate` via React Query while a job is in
 * flight and refetches the UILM diff once the status terminates.
 */

import { graphqlClient } from '@/graphql/graphql-client';
import { toScalarListVariables } from '@/graphql/list-query';
import {
  INSERT_UILM_TRANSLATION_JOB_MUTATION,
  UPDATE_UILM_TRANSLATION_JOB_MUTATION,
} from '@/graphql/mutations';
import { GET_UILM_TRANSLATION_JOBS_QUERY } from '@/graphql/queries';
import type {
  UdsUilmTranslationJob,
  UilmTranslationJob,
  UilmTranslationJobInsertInput,
  UilmTranslationJobStatus,
  UilmTranslationJobUpdateInput,
} from '../types/uilm-translation-job.types';

const normalizeJobStatus = (status: string): UilmTranslationJobStatus => {
  switch (status) {
    case 'queued':
    case 'running':
    case 'completed':
    case 'failed':
      return status;
    default:
      return 'queued';
  }
};

const toJob = (raw: UdsUilmTranslationJob): UilmTranslationJob => ({
  id: raw.ItemId,
  createdAt: raw.CreatedDate,
  updatedAt: raw.LastUpdatedDate,
  messageCoRelationId: raw.MessageCoRelationId,
  templateId: raw.TemplateId,
  templateSlug: raw.TemplateSlug,
  moduleId: raw.ModuleId,
  defaultLanguage: raw.DefaultLanguage,
  status: normalizeJobStatus(raw.Status),
  startedAt: raw.StartedAt,
  completedAt: raw.CompletedAt,
  errorMessage: raw.ErrorMessage,
});

/** Insert a new translation-job row in `running` state. Returns the new ItemId. */
export const createTranslationJob = async (
  input: UilmTranslationJobInsertInput
): Promise<string> => {
  const response = await graphqlClient.request(INSERT_UILM_TRANSLATION_JOB_MUTATION, input);
  const itemId = response?.insertUilmTranslationJob?.itemId;
  if (!itemId) throw new Error('UilmTranslationJob insert returned no ItemId');
  return itemId;
};

/**
 * Patch an existing row. Used by the frontend if `translateAll` fails
 * before the webhook has a chance to flip status — sets `Status: 'failed'`
 * + `ErrorMessage` so the banner shows the cause.
 */
export const updateTranslationJob = async (
  itemId: string,
  input: UilmTranslationJobUpdateInput
): Promise<void> => {
  await graphqlClient.request(UPDATE_UILM_TRANSLATION_JOB_MUTATION, { ItemId: itemId, ...input });
};

/**
 * Return the most-recent translation job for a template (or `null` if
 * none exist). Most-recent = highest `CreatedDate`.
 *
 * The Translations Tab uses this both to drive the banner and to decide
 * whether to keep polling. Polling stops once `Status` terminates
 * (`completed` or `failed`).
 */
export const getLatestTranslationJobForTemplate = async (
  templateId: string
): Promise<UilmTranslationJob | null> => {
  const response = await graphqlClient.query({
    query: GET_UILM_TRANSLATION_JOBS_QUERY,
    variables: toScalarListVariables(
      {
        where: { TemplateId: { eq: templateId } },
        paging: { pageNo: 1, pageSize: 1 },
      },
      { templateId: 'TemplateId' }
    ),
  });
  const items: UdsUilmTranslationJob[] = response?.getUilmTranslationJobs?.items ?? [];
  if (items.length === 0) return null;
  return toJob(items[0]);
};
